import { useEffect, useMemo, useState } from "react";
import VideoCard from "@/components/base/VideoCard";
import Pagination from "@/components/base/Pagination";
import FilterBar from "./FilterBar";
import useIsDesktop from "@/hooks/useIsDesktop";
import { applyFilters, applySort } from "@/data/videos";
import type { Video, FilterState } from "@/data/videos";

interface VideoBrowserProps {
  videos: Video[];
  typeOptions: string[];
  pageSize?: number;
  emptyTitle?: string;
  emptyHint?: string;
}

const emptyFilter: FilterState = { year: "全部", region: "全部", type: "全部" };

export default function VideoBrowser({
  videos,
  typeOptions,
  pageSize = 8,
  emptyTitle = "暂无符合条件的视频",
  emptyHint = "试试更换筛选条件，重置后即可看到全部内容",
}: VideoBrowserProps) {
  const isDesktop = useIsDesktop();
  const [filter, setFilter] = useState<FilterState>(emptyFilter);
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(pageSize);

  useEffect(() => {
    setFilter(emptyFilter);
    setSort("default");
    setPage(1);
    setVisibleCount(pageSize);
  }, [videos, pageSize]);

  const filtered = useMemo(
    () => applySort(applyFilters(videos, filter), sort),
    [videos, filter, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const shown = isDesktop
    ? filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filtered.slice(0, visibleCount);

  const reset = () => {
    setFilter(emptyFilter);
    setSort("default");
    setPage(1);
    setVisibleCount(pageSize);
  };

  return (
    <div>
      <FilterBar
        filter={filter}
        onChange={(f) => {
          setFilter(f);
          setPage(1);
        }}
        typeOptions={typeOptions}
        sort={sort}
        onSortChange={(s) => {
          setSort(s);
          setPage(1);
        }}
        onReset={reset}
      />

      <p className="mt-4 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 个结果
      </p>

      {filtered.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
            {emptyTitle}
          </h3>
          <p className="mt-1 text-sm text-foreground-500">{emptyHint}</p>
          <button
            type="button"
            onClick={reset}
            className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {shown.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {isDesktop ? (
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          ) : visibleCount < filtered.length ? (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((c) => c + pageSize)}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-add-line"></i>
                加载更多
              </button>
            </div>
          ) : (
            <p className="mt-6 text-center text-xs text-foreground-400">已经到底啦</p>
          )}
        </>
      )}
    </div>
  );
}